/**
 * @fileoverview Table-cell sparkline with an overall-change badge.
 * @description Ported from the InsightBox Banking design system's own `charts/TrendCell.tsx` — a
 * small hand-drawn SVG sparkline (no recharts) whose individual segments/dots are colored by their
 * own local movement (red where that year-over-year step fell, green where it rose), plus a bold
 * arrow + percentage badge summarizing the overall FY-start-to-FY-end change. Used in Bank
 * Overview's 5-year table's "Trend" column. Feature-scoped rather than promoted to `shared/` since
 * it's tied to this app's `CHART_SUCCESS`/`CHART_ERROR` semantics used elsewhere in banking charts.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { CHART_ERROR, CHART_SUCCESS } from '@/shared/constants/chart-theme.constants'

interface TrendCellProps {
  data: number[]
  width?: number
  height?: number
  /** "right" flushes the sparkline + badge to the right edge of the cell (last column). */
  align?: 'left' | 'right'
}

/**
 * @component
 */
function TrendCell({ data, width = 96, height = 34, align = 'left' }: TrendCellProps) {
  const first = data[0]!
  const last = data[data.length - 1]!
  const pct = first !== 0 ? ((last - first) / first) * 100 : 0
  const overallUp = last >= first
  const badgeColor = overallUp ? CHART_SUCCESS : CHART_ERROR

  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1
  const padY = height * 0.2
  const stepX = width / (data.length - 1)
  const points = data.map((v, i) => ({
    x: i * stepX,
    y: height - padY - ((v - min) / range) * (height - padY * 2),
  }))

  return (
    <div className={`flex items-center gap-3 ${align === 'right' ? 'justify-end' : ''}`}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="shrink-0 overflow-visible"
      >
        {points.slice(1).map((p, i) => {
          const prev = points[i]!
          const segUp = data[i + 1]! >= data[i]!
          return (
            <line
              key={i}
              x1={prev.x}
              y1={prev.y}
              x2={p.x}
              y2={p.y}
              stroke={segUp ? CHART_SUCCESS : CHART_ERROR}
              strokeWidth={2}
              strokeLinecap="round"
            />
          )
        })}
        {points.map((p, i) => {
          const dotUp = i === 0 || data[i]! >= data[i - 1]!
          return (
            <circle key={i} cx={p.x} cy={p.y} r={2.75} fill={dotUp ? CHART_SUCCESS : CHART_ERROR} />
          )
        })}
      </svg>
      <span className="flex items-center gap-1 text-sm font-bold" style={{ color: badgeColor }}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d={overallUp ? 'M12 19V5M5 12l7-7 7 7' : 'M12 5v14M5 12l7 7 7-7'} />
        </svg>
        {Math.abs(pct).toFixed(1)}%
      </span>
    </div>
  )
}

export default TrendCell
